import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

function RTCDot(props) {
  const { cx, cy, payload, stroke, active } = props;
  if (cx == null || cy == null) return null;

  const r = active ? 6 : 4;

  const handleClick = (e) => {
    e?.stopPropagation?.();
    const monthKey = payload?.month_key;
    if (monthKey) {
      window.dispatchEvent(new CustomEvent('analytics:rtcMonthClick', { detail: { monthKey } }));
    }
  };

  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={stroke}
      stroke="#ffffff"
      strokeWidth={2}
      style={{ cursor: 'pointer' }}
      onClick={handleClick}
    />
  );
}
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];


const P2P_COLORS = {
  Compliant: '#16a34a',
  Due: '#f59e0b',
  Overdue: '#dc2626',
};


const BLAMEWORTHY_COLORS = {
  'Non-Blameworthy': '#16a34a',
  Blameworthy: '#dc2626',
  'Not Set': '#94a3b8',
};

function formatIsoDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return '—';
  }
}

function exportDialogToCSV(dialog) {
  if (!dialog || !dialog.items?.length) return;

  const esc = (val) => {
    if (val == null) return '';
    const s = String(val);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };

  let headers, rows;
  const items = dialog.items;

  switch (dialog.type) {
    case 'p2p':
      headers = ['Driver Name', 'Employee ID', 'Last Completed P2P', 'P2P Count', 'Last RTC'];
      rows = items.map((d) => [
        d.driver_name, d.employee_id || '', formatIsoDate(d.meta?.last_completed),
        d.meta?.p2p_count ?? 0, formatIsoDate(d.last_accident_date),
      ]);
      break;
    case 'severity':
      headers = ['Driver Name', 'Employee ID', 'RTC Count', 'Last RTC', 'Last Assessment'];
      rows = items.map((d) => [
        d.driver_name, d.employee_id || '', d.meta?.accident_count || 0,
        formatIsoDate(d.last_accident_date), formatIsoDate(d.last_assessment_date),
      ]);
      break;
    case 'blameworthy':
      headers = ['Driver Name', 'Employee ID', 'Category RTCs', 'Last RTC', 'Last Assessment'];
      rows = items.map((d) => [
        d.driver_name, d.employee_id || '', d.meta?.category_rtc_count || 0,
        formatIsoDate(d.last_accident_date), formatIsoDate(d.last_assessment_date),
      ]);
      break;
    case 'rtc_month':
      headers = ['Driver Name', 'Employee ID', 'RTCs in Month', 'Last RTC', 'Last Assessment'];
      rows = items.map((d) => [
        d.driver_name, d.employee_id || '', d.meta?.accident_count || 0,
        formatIsoDate(d.last_accident_date), formatIsoDate(d.last_assessment_date),
      ]);
      break;
    case 'assessment_type':
      headers = ['Driver Name', 'Employee ID', 'Assessment Count', 'Last Assessment', 'Last RTC'];
      rows = items.map((d) => [
        d.driver_name, d.employee_id || '', d.meta?.assessment_count || 0,
        formatIsoDate(d.last_assessment_date), formatIsoDate(d.last_accident_date),
      ]);
      break;
    case 'training_course':
      headers = ['Driver Name', 'Employee ID', 'Last Assessment', 'Last RTC'];
      rows = items.map((d) => [
        d.driver_name, d.employee_id || '',
        formatIsoDate(d.last_assessment_date), formatIsoDate(d.last_accident_date),
      ]);
      break;
    default:
      headers = ['Driver Name', 'Employee ID', 'Last Assessment', 'Last RTC'];
      rows = items.map((d) => [
        d.driver_name, d.employee_id || '',
        formatIsoDate(d.last_assessment_date), formatIsoDate(d.last_accident_date),
      ]);
  }

  const csv = [headers.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeName = (dialog.title || 'export').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_');
  a.download = `${safeName}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function buildAnalyticsQuery(filters) {
  const params = new URLSearchParams();
  const shift = filters?.shift;
  if (shift && shift !== 'All') params.set('shift', shift);
  if (filters?.agencyOnly) params.set('employment_type', 'Agency');
  if (filters?.includeInactive) params.set('include_inactive', 'true');
  return params.toString();
}


function DriverListRow({ driver, onOpenDriver, rightBadgeText, mode = 'default' }) {
  return (
    <div
      className="p-4 border dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
      onClick={onOpenDriver}
      role="button"
      tabIndex={0}
    >
      <div className="flex justify-between items-start gap-3">
        <div>
          <p className="font-medium text-slate-800 dark:text-slate-200">{driver.driver_name}</p>
          <div className="mt-1 space-y-0.5">
            <p className="text-sm text-slate-600 dark:text-slate-400">Employee ID: {driver.employee_id || '—'}</p>

            {mode === 'p2p' ? (
              <>
                <p className="text-xs text-slate-500 dark:text-slate-500">Last completed P2P: {formatIsoDate(driver?.meta?.last_completed)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-500">P2P completed count: {driver?.meta?.p2p_count ?? 0}</p>
              </>
            ) : (
              driver.last_assessment_date && (
                <p className="text-xs text-slate-500 dark:text-slate-500">Last assessment: {formatIsoDate(driver.last_assessment_date)}</p>
              )
            )}

            {driver.last_accident_date && (
              <p className="text-xs text-slate-500 dark:text-slate-500">Last RTC: {formatIsoDate(driver.last_accident_date)}</p>
            )}
          </div>
        </div>
        {rightBadgeText ? <Badge variant="secondary">{rightBadgeText}</Badge> : null}
      </div>
      <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">Open driver →</p>
    </div>
  );
}

export default function Analytics() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  const [globalFilters, setGlobalFilters] = useState({
    shift: 'All',
    agencyOnly: false,
    includeInactive: false,
  });


  const [dialog, setDialog] = useState(null);
  // dialog = { type, title, description, loading, items }

  const closeDialog = () => setDialog(null);

  const openDriver = useCallback((driverId) => {
    closeDialog();
    navigate(`/drivers/${driverId}`);
  }, [navigate]);

  function driversFromAnalyticsMap(driverList) {
    if (!Array.isArray(driverList)) return [];
    return driverList.map((d) => ({
      driver_id: d.driver_id,
      driver_name: d.driver_name,
      employee_id: d.employee_id,
      last_assessment_date: d.last_assessment_date,
      last_accident_date: d.last_accident_date,
      meta: d,
    }));
  }

  const openRTCMonthDialog = useCallback(async (monthKey) => {
    const q = buildAnalyticsQuery(globalFilters);
    const qStr = q ? `?${q}` : '';

    setDialog({
      type: 'rtc_month',
      title: `RTC Drivers: ${monthKey}`,
      description: 'Drivers with RTCs in this month',
      loading: true,
      items: [],
    });

    try {
      const res = await axios.get(`${API}/analytics/drivers-by-rtc-month/${monthKey}${qStr}`, getAuthHeaders());
      setDialog((d) => ({ ...d, loading: false, items: driversFromAnalyticsMap(res.data) }));
    } catch (e) {
      toast.error('Failed to load driver list');
      setDialog((d) => ({ ...d, loading: false }));
    }
  }, [globalFilters]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const q = buildAnalyticsQuery(globalFilters);
      const url = `${API}/analytics?_t=${Date.now()}${q ? `&${q}` : ''}`;
      const res = await axios.get(url, getAuthHeaders());
      setAnalytics(res.data);
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [globalFilters]);

  useEffect(() => {
    fetchAnalytics();

    const handler = (e) => {
      const monthKey = e?.detail?.monthKey;
      if (monthKey) openRTCMonthDialog(monthKey);
    };

    window.addEventListener('analytics:rtcMonthClick', handler);
    return () => window.removeEventListener('analytics:rtcMonthClick', handler);
  }, [fetchAnalytics, openRTCMonthDialog]);

  const openAllDriversDialog = useCallback(async ({ shift = null, agencyOnly = false, includeInactive = false } = {}) => {

    setDialog({
      type: 'all_drivers',
      title: 'All Drivers',
      description: 'Filter and click a driver to open their record',
      loading: true,
      items: [],
      filters: {
        shift: shift || 'All',
        agencyOnly: !!agencyOnly,
        includeInactive: !!includeInactive,
      },
    });

    try {
      const params = new URLSearchParams();
      if (shift && shift !== 'All') params.set('shift', shift);
      if (agencyOnly) params.set('employment_type', 'Agency');
      params.set('include_inactive', includeInactive ? 'true' : 'false');

      const res = await axios.get(`${API}/analytics/drivers?${params.toString()}`, getAuthHeaders());
      setDialog((d) => ({ ...d, loading: false, items: driversFromAnalyticsMap(res.data) }));
    } catch (e) {
      toast.error('Failed to load driver list');
      setDialog((d) => ({ ...d, loading: false }));
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
    // When the global filters change, refresh the open "All Drivers" dialog list.
    if (dialog?.type === 'all_drivers') {
      openAllDriversDialog({
        shift: globalFilters.shift,
        agencyOnly: globalFilters.agencyOnly,
        includeInactive: globalFilters.includeInactive,
      });
    }
  }, [globalFilters, dialog?.type, fetchAnalytics, openAllDriversDialog]);


  const openP2PComplianceDialog = async (statusName) => {
    const q = buildAnalyticsQuery(globalFilters);
    const qStr = q ? `?${q}` : '';
    setDialog({
      type: 'p2p',
      title: `P2P Compliance: ${statusName}`,
      description: 'Drivers in this P2P compliance category',
      loading: true,
      items: [],
    });

    try {
      const res = await axios.get(`${API}/analytics/drivers-by-compliance/${statusName}${qStr}`, getAuthHeaders());
      setDialog((d) => ({ ...d, loading: false, items: driversFromAnalyticsMap(res.data) }));
    } catch (e) {
      toast.error('Failed to load driver list');
      setDialog((d) => ({ ...d, loading: false }));
    }
  };

  const openSeverityDialog = async (severity) => {
    const q = buildAnalyticsQuery(globalFilters);
    const qStr = q ? `?${q}` : '';
    setDialog({
      type: 'severity',
      title: `Accident Severity: ${severity}`,
      description: 'Drivers with RTCs matching this severity',
      loading: true,
      items: [],
    });

    try {
      const res = await axios.get(`${API}/analytics/drivers-by-severity/${severity}${qStr}`, getAuthHeaders());
      setDialog((d) => ({ ...d, loading: false, items: driversFromAnalyticsMap(res.data) }));
    } catch (e) {
      toast.error('Failed to load driver list');
      setDialog((d) => ({ ...d, loading: false }));
    }
  };

  const openBlameworthyDialog = (categoryName) => {
    const list = analytics?.blameworthy_drivers?.[categoryName] || [];
    setDialog({
      type: 'blameworthy',
      title: `Blameworthy: ${categoryName}`,
      description: 'Drivers grouped by blameworthy decision',
      loading: false,
      items: driversFromAnalyticsMap(list),
    });
  };

  const openAssessmentTypeDialog = async (assessmentTypeName) => {
    const q = buildAnalyticsQuery(globalFilters);
    const qStr = q ? `?${q}` : '';
    setDialog({
      type: 'assessment_type',
      title: `Assessment Type: ${assessmentTypeName}`,
      description: 'Drivers with this assessment type (any status)',
      loading: true,
      items: [],
    });

    try {
      const res = await axios.get(
        `${API}/analytics/drivers-by-assessment-type/${encodeURIComponent(assessmentTypeName)}${qStr}`,
        getAuthHeaders()
      );
      setDialog((d) => ({ ...d, loading: false, items: driversFromAnalyticsMap(res.data) }));
    } catch (e) {
      toast.error('Failed to load driver list');
      setDialog((d) => ({ ...d, loading: false }));
    }
  };

  const openTrainingCourseDialog = async (courseName, bucket) => {
    const q = buildAnalyticsQuery(globalFilters);
    const qStr = q ? `&${q}` : '';
    const label = bucket === 'completed' ? 'Completed' : 'Scheduled';
    setDialog({
      type: 'training_course',
      title: `Training Course: ${courseName} — ${label}`,
      description: 'Drivers by course and completion bucket',
      loading: true,
      items: [],
    });

    try {
      const res = await axios.get(
        `${API}/analytics/drivers-by-training-course?course=${encodeURIComponent(courseName)}&bucket=${bucket}${qStr}`,
        getAuthHeaders()
      );
      setDialog((d) => ({ ...d, loading: false, items: driversFromAnalyticsMap(res.data) }));
    } catch (e) {
      toast.error('Failed to load driver list');
      setDialog((d) => ({ ...d, loading: false }));
    }
  };

  const handleTrainingBarClick = (rechartsData, bucket) => {
    const payload = rechartsData?.payload || rechartsData;
    const courseName = payload?.course_full || payload?.course;
    if (!courseName) return;
    openTrainingCourseDialog(courseName, bucket);
  };

  const handleRTCBarClick = (rechartsData) => {
    const payload = rechartsData?.payload || rechartsData;
    const monthKey = payload?.month_key;
    if (!monthKey) return;
    openRTCMonthDialog(monthKey);
  };

  // (defined earlier) openRTCMonthDialog

  const metricCards = useMemo(() => {
    if (!analytics) return [];
    return [
      {
        label: 'All Drivers',
        value: 'View',
        hint: 'Drill-down list',
        onClick: () =>
          openAllDriversDialog({
            shift: globalFilters.shift,
            agencyOnly: globalFilters.agencyOnly,
            includeInactive: globalFilters.includeInactive,
          }),
        clickable: true,
      },
      {
        label: 'P2P Compliance',
        value: `${analytics.p2p_compliance_rate || 0}%`,
        hint: 'Within last year',
      },
      {
        label: 'Training Completion',
        value: `${analytics.training_completion_rate || 0}%`,
        hint: 'This month',
      },
      {
        label: 'Accident-Free Drivers',
        value: `${analytics.accident_free_percentage || 0}%`,
        hint: `${analytics.accident_free_count || 0} drivers`,
      },
      {
        label: 'Avg RTC/Driver',
        value: `${analytics.avg_rtc_per_driver ?? 0}`,
        hint: 'Fleet average',
      },
    ];
  }, [analytics, openAllDriversDialog, globalFilters.shift, globalFilters.agencyOnly, globalFilters.includeInactive]);

  if (loading) return <div className="text-center py-12">Loading analytics…</div>;
  if (!analytics) return <div className="text-center py-12">No analytics data available</div>;

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div>
        <h1 className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2">Analytics</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Drill into assessment and RTC patterns. Click any chart segment to view the driver list.
        </p>
      </div>

      {/* Global Filters */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {['All', 'Early', 'Late', 'Night', 'Part-Time'].map((s) => (
              <Button
                key={s}
                variant={globalFilters.shift === s ? 'default' : 'outline'}
                size="sm"
                onClick={() => setGlobalFilters({ ...globalFilters, shift: s })}
              >
                {s}
              </Button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <Switch
                checked={!!globalFilters.agencyOnly}
                onCheckedChange={(v) => setGlobalFilters({ ...globalFilters, agencyOnly: v })}
              />
              Agency only
            </label>

            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <Switch
                checked={!!globalFilters.includeInactive}
                onCheckedChange={(v) => setGlobalFilters({ ...globalFilters, includeInactive: v })}
              />
              Include inactive
            </label>
          </div>
        </CardContent>
      </Card>


      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {metricCards.map((c) => (
          <Card
            key={c.label}
            className={c.clickable ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900' : ''}
            onClick={c.onClick}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-slate-300">{c.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-800 dark:text-slate-100">{c.value}</div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{c.hint}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">P2P Compliance Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={analytics.compliance_status}
                  cx="50%"
                  cy="50%"
                  outerRadius={105}
                  dataKey="value"
                  labelLine={false}
                  label={(e) => `${e.name}: ${e.value}`}
                >
                  {analytics.compliance_status.map((entry, index) => (
                    <Cell
                      key={`p2p-${entry.name}`}
                      fill={P2P_COLORS[entry.name] || COLORS[index % COLORS.length]}
                      onClick={() => openP2PComplianceDialog(entry.name)}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <p className="text-xs text-center text-slate-500 dark:text-slate-400 mt-2">Click a slice to view drivers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Assessment Types</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={analytics.assessment_types} margin={{ bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={90} fontSize={12} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6">
                  {analytics.assessment_types.map((entry) => (
                    <Cell
                      key={`atype-${entry.key || entry.name}`}
                      onClick={() => openAssessmentTypeDialog(entry.key || entry.name)}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-center text-slate-500 dark:text-slate-400 mt-2">Click a bar to view drivers</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Training Completion by Course</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={360}>
              <BarChart data={analytics.training_courses} margin={{ bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="course" angle={-45} textAnchor="end" height={95} fontSize={11} />
                <YAxis />
                <Tooltip />
                <Bar
                  dataKey="completed"
                  fill="#10b981"
                  name="Completed"
                  onClick={(d) => handleTrainingBarClick(d, 'completed')}
                  style={{ cursor: 'pointer' }}
                >
                  {analytics.training_courses.map((entry) => (
                    <Cell
                      key={`tc-c-${entry.course_full || entry.course}`}
                      onClick={() => openTrainingCourseDialog(entry.course_full || entry.course, 'completed')}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Bar>
                <Bar
                  dataKey="scheduled"
                  fill="#f59e0b"
                  name="Not Completed"
                  onClick={(d) => handleTrainingBarClick(d, 'scheduled')}
                  style={{ cursor: 'pointer' }}
                >
                  {analytics.training_courses.map((entry) => (
                    <Cell
                      key={`tc-s-${entry.course_full || entry.course}`}
                      onClick={() => openTrainingCourseDialog(entry.course_full || entry.course, 'scheduled')}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-center text-slate-500 dark:text-slate-400 mt-2">Click bars to view drivers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">RTC Trends (6 months)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={analytics.rtc_trends} margin={{ bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" angle={-45} textAnchor="end" height={70} fontSize={12} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="accidents"
                  stroke="#ef4444"
                  strokeWidth={2}
                  name="Total RTCs"
                  dot={<RTCDot />}
                  activeDot={<RTCDot active />}
                />
                <Line
                  type="monotone"
                  dataKey="blameworthy"
                  stroke="#f97316"
                  strokeWidth={2}
                  name="Blameworthy"
                  dot={<RTCDot />}
                  activeDot={<RTCDot active />}
                />
              </LineChart>
            </ResponsiveContainer>
            <p className="text-xs text-center text-slate-500 dark:text-slate-400 mt-2">Click a point to view drivers</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Blameworthy Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={analytics.blameworthy_breakdown}
                  cx="50%"
                  cy="50%"
                  outerRadius={105}
                  dataKey="value"
                  labelLine={false}
                  label={(e) => `${e.name}: ${e.value}`}
                >
                  {analytics.blameworthy_breakdown.map((entry) => (
                    <Cell
                      key={`bw-${entry.name}`}
                      fill={BLAMEWORTHY_COLORS[entry.name] || '#94a3b8'}
                      onClick={() => openBlameworthyDialog(entry.name)}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <p className="text-xs text-center text-slate-500 dark:text-slate-400 mt-2">Click a slice to view drivers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Accident Severity</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={analytics.severity_distribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="severity" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#f59e0b">
                  {analytics.severity_distribution.map((entry) => (
                    <Cell
                      key={`sev-${entry.severity}`}
                      onClick={() => openSeverityDialog(entry.severity)}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-center text-slate-500 dark:text-slate-400 mt-2">Click a bar to view drivers</p>
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!dialog} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                <DialogTitle>{dialog?.title}</DialogTitle>
                <DialogDescription>{dialog?.description}</DialogDescription>
              </div>
              {!dialog?.loading && dialog?.items?.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportDialogToCSV(dialog)}
                  data-testid="export-csv-button"
                >
                  <Download className="h-4 w-4 mr-1" />
                  CSV
                </Button>
              )}
            </div>
          </DialogHeader>

          {dialog?.type === 'all_drivers' && (
            <div className="mt-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                {['All', 'Early', 'Late', 'Night', 'Part-Time'].map((s) => (
                  <Button
                    key={s}
                    variant={globalFilters.shift === s ? 'default' : 'outline'}
                    size="sm"
                    onClick={() =>
                      setGlobalFilters({
                        ...globalFilters,
                        shift: s,
                      })
                    }
                  >
                    {s}
                  </Button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <Switch
                    checked={!!globalFilters.agencyOnly}
                    onCheckedChange={(v) =>
                      setGlobalFilters({
                        ...globalFilters,
                        agencyOnly: v,
                      })
                    }
                  />
                  Agency only
                </label>

                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <Switch
                    checked={!!globalFilters.includeInactive}
                    onCheckedChange={(v) =>
                      setGlobalFilters({
                        ...globalFilters,
                        includeInactive: v,
                      })
                    }
                  />
                  Include inactive
                </label>
              </div>
            </div>
          )}


          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            {dialog?.loading ? (
              <p className="text-center py-10 text-slate-500 dark:text-slate-400">Loading…</p>
            ) : (dialog?.items || []).length === 0 ? (
              <p className="text-center py-10 text-slate-500 dark:text-slate-400">No drivers found</p>
            ) : (
              dialog.items.map((d) => (
                <DriverListRow
                  key={d.driver_id}
                  driver={d}
                  onOpenDriver={() => openDriver(d.driver_id)}
                  mode={dialog.type === 'p2p' ? 'p2p' : 'default'}
                  rightBadgeText={
                    dialog.type === 'severity'
                      ? `${d.meta?.accident_count || 0} RTC(s)`
                      : dialog.type === 'p2p'
                        ? dialog.title?.split(': ')[1]
                        : dialog.type === 'training_course'
                          ? 'Training'
                          : dialog.type === 'assessment_type'
                            ? 'Assessment'
                            : dialog.type === 'rtc_month'
                              ? `${d.meta?.accident_count || 0} RTC(s)`
                              : dialog.type === 'blameworthy'
                                ? `${d.meta?.category_rtc_count || 0} RTC(s)`
                                : undefined
                  }
                />
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
